import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

/**
 * Single Lenis instance — the canonical scroll driver for the whole site.
 *
 * Adapted from legacy lines 2593-2607. Differences:
 *  - Exposed as a singleton import rather than IIFE-scoped.
 *  - scrollState exported so any scene/scene can read normalized velocity without
 *    monkey-patching the lenis instance.
 *  - duration tuned to 1.1 (1.2 in legacy felt slightly laggy on M1 trackpads).
 */
export const lenis = new Lenis({
  duration: 1.1,
  smoothWheel: true,
  easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
});

export const scrollState = {
  /** Raw Lenis velocity */
  velocity: 0,
  /** Normalized to ~[-1, 1] for shader uniforms */
  normalizedVelocity: 0,
  /** 0..1 progress over the whole page */
  progress: 0,
};

lenis.on("scroll", (e: { velocity?: number; progress?: number }) => {
  scrollState.velocity = e.velocity ?? 0;
  // Lenis velocity is in pixels/ms typically — normalize roughly to [-1, 1]
  scrollState.normalizedVelocity = Math.max(-1, Math.min(1, scrollState.velocity / 30));
  scrollState.progress = e.progress ?? 0;
  ScrollTrigger.update();
});

gsap.ticker.add((time: number) => lenis.raf(time * 1000));
gsap.ticker.lagSmoothing(0);

ScrollTrigger.scrollerProxy(document.body, {
  scrollTop(value?: number) {
    if (value !== undefined) {
      lenis.scrollTo(value, { immediate: true });
      return undefined;
    }
    return window.scrollY;
  },
  getBoundingClientRect() {
    return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
  },
});

ScrollTrigger.defaults({ scroller: document.body });

export { ScrollTrigger };
